import type { GeneratedRequest } from './types';

/**
 * Registered in `atlassian-cookies` as a STARTS_WITH pattern under `Products.ALL`, covering
 * everything after it. Keys losing this prefix are blocked in every product.
 */
const CACHE_KEY_PREFIX = 'rovo-potd:';

/** Bump when the shape stored under a key changes incompatibly. */
const CACHE_KEY_VERSION = 'v1';

/** Key order is not part of the request, so it must not change the entry a caller lands on. */
const stableStringify = (value: unknown): string => {
	if (Array.isArray(value)) {
		return `[${value.map(stableStringify).join(',')}]`;
	}

	if (typeof value === 'object' && value !== null) {
		const entries = Object.entries(value)
			.filter(([, entryValue]) => entryValue !== undefined)
			.sort(([left], [right]) => (left < right ? -1 : 1))
			.map(([key, entryValue]) => `${JSON.stringify(key)}:${stableStringify(entryValue)}`);
		return `{${entries.join(',')}}`;
	}

	return JSON.stringify(value) ?? 'null';
};

const getArgsFingerprint = (args: GeneratedRequest['args']): string => {
	const fingerprint = args === undefined ? '{}' : stableStringify(args);
	return fingerprint === '{}' ? 'default' : fingerprint;
};

/**
 * Derived from the request, never supplied by the caller, so surfaces sending the same request
 * cannot land on separate entries and issue the same POST twice. Anything not in the request stays
 * out - a calling surface's name belongs in analytics attributes.
 */
export const getGeneratedCacheKey = ({ slug, accountId, args }: GeneratedRequest): string =>
	`${CACHE_KEY_PREFIX}${CACHE_KEY_VERSION}:${slug}:${accountId}:${getArgsFingerprint(args)}`;
